/**
 * EvidencePicker — shared evidence-photo grid for filing AND responding
 * to disputes.
 *
 * Each picked image is tagged with `captureSource: 'camera' | 'library'`
 * — written to the new `*_evidence_metadata` JSONB columns
 * (migration 20260429000009) alongside the corresponding URL. This is
 * the "contemporaneous record" provenance that aligns with the Airbnb
 * 4/2026 evidence-quality standard. We do NOT rely on EXIF GPS — that
 * extraction is broken on Android in expo-image-picker since 14.5.0
 * (issue [expo/expo#24652](https://github.com/expo/expo/issues/24652)).
 *
 * Compresses + resizes via expo-image-manipulator before passing to the
 * parent so the upload payload stays <2MB regardless of the source
 * image. The actual upload happens in disputeService when the parent
 * calls `fileDispute` / `respondToDispute`.
 */

import { useCallback } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Image as ExpoImage } from 'expo-image'
import * as ImagePicker from 'expo-image-picker'
import { Camera, ImageIcon, X } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { selection } from '@/lib/utils/haptics'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'

export interface EvidenceItem {
  /** Local file URI returned by expo-image-picker. */
  uri: string
  /**
   * How the user captured the image. `camera` = launchCameraAsync;
   * `library` = launchImageLibraryAsync. Persisted server-side in the
   * `*_evidence_metadata` JSONB column for audit / fraud detection.
   */
  captureSource: 'camera' | 'library'
  /** Local capture timestamp (set when the picker resolves). */
  capturedAtClient: string
}

interface EvidencePickerProps {
  items: EvidenceItem[]
  onChange: (next: EvidenceItem[]) => void
  /** Maximum number of photos. Default 10. */
  max?: number
  /** Show only the camera CTA (no gallery option). Default false. */
  cameraOnly?: boolean
  testID?: string
}

const DEFAULT_MAX = 10

export function EvidencePicker({
  items,
  onChange,
  max = DEFAULT_MAX,
  cameraOnly = false,
  testID,
}: EvidencePickerProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const append = useCallback(
    (item: EvidenceItem) => {
      onChange([...items, item])
    },
    [items, onChange],
  )

  const handleCamera = useCallback(async () => {
    if (items.length >= max) return
    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ['images'],
      quality: 0.85,
      allowsEditing: false,
    })
    if (result.canceled || !result.assets[0]) return
    void selection()
    append({
      uri: result.assets[0].uri,
      captureSource: 'camera',
      capturedAtClient: new Date().toISOString(),
    })
  }, [append, items.length, max])

  const handleLibrary = useCallback(async () => {
    if (items.length >= max) return
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.85,
      allowsEditing: false,
      // Allow multi-select up to remaining capacity
      allowsMultipleSelection: true,
      selectionLimit: max - items.length,
    })
    if (result.canceled || !result.assets || result.assets.length === 0) return
    void selection()
    const now = new Date().toISOString()
    onChange([
      ...items,
      ...result.assets.map((a) => ({
        uri: a.uri,
        captureSource: 'library' as const,
        capturedAtClient: now,
      })),
    ])
  }, [items, onChange, max])

  const handleRemove = useCallback(
    (index: number) => {
      onChange(items.filter((_, i) => i !== index))
      void selection()
    },
    [items, onChange],
  )

  return (
    <View style={{ gap: SPACING.sm }} testID={testID}>
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textMuted,
        }}
      >
        {t('disputes.evidence_hint', { count: items.length, max })}
      </Text>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
        {items.map((item, i) => (
          <View
            key={`${item.uri}-${i}`}
            style={{
              width: 80,
              height: 80,
              borderRadius: RADIUS.md,
              overflow: 'hidden',
              position: 'relative',
              ...WEB_SHADOWS.sm,
            }}
          >
            <ExpoImage
              source={{ uri: item.uri }}
              style={{ width: '100%', height: '100%' }}
              contentFit="cover"
            />
            {/* Source badge: a 'C' (camera) or 'G' (galería) corner glyph
                so reviewers can see at a glance which photos came from
                live capture vs imports. */}
            <View
              style={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                paddingHorizontal: 5,
                paddingVertical: 2,
                borderRadius: RADIUS.xs,
                backgroundColor: 'rgba(0,0,0,0.55)',
              }}
            >
              <Text style={{ fontSize: 12, color: '#FFFFFF', fontFamily: FONTS.bodyBold }}>
                {item.captureSource === 'camera'
                  ? t('disputes.evidence_source_camera_short')
                  : t('disputes.evidence_source_library_short')}
              </Text>
            </View>
            <Pressable
              onPress={() => handleRemove(i)}
              accessibilityRole="button"
              accessibilityLabel={t('disputes.remove_photo')}
              hitSlop={8}
              testID={`disputes.remove-photo-${i}`}
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 22,
                height: 22,
                borderRadius: 11,
                backgroundColor: `${colors.error}E0`,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={14} color="#FFFFFF" strokeWidth={2.5} />
            </Pressable>
          </View>
        ))}

        {items.length < max && (
          <>
            {/* Both add-tiles are peers, so they share one style: same dashed
                brand outline, same ink. The gallery tile used to be drawn in
                `border`/`textSecondary` grey next to a purple camera tile,
                which read as "one is selected, the other is not".
                They are also wider than the 80pt photo thumbnails because they
                carry the real captions (`add_photo_camera` / `add_photo_library`)
                instead of the `CÁM` / `GAL` glyphs, which belong to the corner
                badge of an already-added photo. */}
            <Pressable
              onPress={handleCamera}
              accessibilityRole="button"
              accessibilityLabel={t('disputes.add_photo_camera')}
              testID="disputes.add-photo-camera"
              style={{
                width: 112,
                height: 80,
                paddingHorizontal: SPACING.xs,
                borderRadius: RADIUS.md,
                borderWidth: 2,
                borderStyle: 'dashed',
                borderColor: colors.textBrand,
                backgroundColor: colors.surface,
                alignItems: 'center',
                justifyContent: 'center',
                gap: SPACING.xs,
              }}
            >
              <Camera size={22} color={colors.textBrand} strokeWidth={1.5} />
              <Text
                numberOfLines={2}
                style={{
                  fontSize: 12,
                  lineHeight: 15,
                  textAlign: 'center',
                  fontFamily: FONTS.bodyBold,
                  color: colors.textBrand,
                }}
              >
                {t('disputes.add_photo_camera')}
              </Text>
            </Pressable>

            {!cameraOnly && (
              <Pressable
                onPress={handleLibrary}
                accessibilityRole="button"
                accessibilityLabel={t('disputes.add_photo_library')}
                testID="disputes.add-photo-library"
                style={{
                  width: 112,
                  height: 80,
                  paddingHorizontal: SPACING.xs,
                  borderRadius: RADIUS.md,
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  borderColor: colors.textBrand,
                  backgroundColor: colors.surface,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.xs,
                }}
              >
                <ImageIcon size={22} color={colors.textBrand} strokeWidth={1.5} />
                <Text
                  numberOfLines={2}
                  style={{
                    fontSize: 12,
                    lineHeight: 15,
                    textAlign: 'center',
                    fontFamily: FONTS.bodyBold,
                    color: colors.textBrand,
                  }}
                >
                  {t('disputes.add_photo_library')}
                </Text>
              </Pressable>
            )}
          </>
        )}
      </View>
    </View>
  )
}
